import { Button, Checkbox, ScrollArea, TabsContent } from '@zond/ui';
import { Download, UserCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from './empty-state';
import { IntegrationItem } from './integration-item';
import { LoadingSkeleton } from './loading-skeleton';
import { getUserEmail, getUserId, getUserName, isGithub } from './utils';

interface UsersTabProps {
  integrationName: string;
  users: any[];
  usersLoading: boolean;
  selectedUsers: Set<string>;
  onUserToggle: (userId: string) => void;
  onSelectAllUsers: () => void;
  onExportUsers: () => void;
  isExportingUsers: boolean;
}

export function UsersTab({
  integrationName,
  users,
  usersLoading,
  selectedUsers,
  onUserToggle,
  onSelectAllUsers,
  onExportUsers,
  isExportingUsers,
}: UsersTabProps) {
  const { t } = useTranslation('integrations');
  const isGithubIntegration = isGithub(integrationName);

  return (
    <TabsContent
      value="users"
      className="mt-0 mx-6 pb-4 max-w-full overflow-x-hidden"
      style={{ maxWidth: '752px', width: '100%' }}
    >
      <div className="flex items-center justify-between mb-4 pt-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id="select-all-users"
            checked={users?.length > 0 && selectedUsers.size === users?.length}
            onCheckedChange={onSelectAllUsers}
          />
          <label
            htmlFor="select-all-users"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {t('list.selectAll', { count: selectedUsers.size })}
          </label>
        </div>
        <Button
          size="sm"
          onClick={onExportUsers}
          disabled={selectedUsers.size === 0 || isExportingUsers}
          className="gap-2"
        >
          <Download className="h-4 w-4" />
          {isExportingUsers ? t('usersTab.inviting') : t('list.exportSelected')}
        </Button>
      </div>

      <ScrollArea
        className="rounded-md border h-[200px] !w-full !max-w-full overflow-x-hidden"
        style={{ maxWidth: '100%', width: '100%' }}
      >
        <div
          className="p-4 !w-full !max-w-full overflow-x-hidden"
          style={{ maxWidth: '100%', width: '100%' }}
        >
          {usersLoading ? (
            <LoadingSkeleton />
          ) : users?.length === 0 ? (
            <EmptyState
              icon={UserCheck}
              title={
                isGithubIntegration ? t('usersTab.empty.collaboratorsTitle') : t('usersTab.empty.usersTitle')
              }
              description={
                isGithubIntegration
                  ? t('usersTab.empty.collaboratorsDescription')
                  : t('usersTab.empty.usersDescription')
              }
            />
          ) : (
            <div className="space-y-2 w-full overflow-x-hidden" style={{ maxWidth: '100%', width: '100%' }}>
              {users?.map((user: any) => {
                const userId = getUserId(user);
                const userName = getUserName(user);
                const userEmail = getUserEmail(user);
                const isSelected = selectedUsers.has(userId);

                return (
                  <IntegrationItem
                    key={userId}
                    id={userId}
                    name={userName}
                    description={userEmail}
                    icon={userName.slice(0, 2).toUpperCase()}
                    isSelected={isSelected}
                    onToggle={onUserToggle}
                    isInactive={user.active === false}
                  />
                );
              })}
            </div>
          )}
        </div>
      </ScrollArea>
    </TabsContent>
  );
}
